import * as React from "react";
import { CalendarDays, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";

// A drop-in replacement for <input type="date">.
//
//   <DateField value="2026-10-06" onChange={(e) => setFrom(e.target.value)} />
//
// value is a "YYYY-MM-DD" string (or ""), exactly like the native input, and
// onChange receives { target: { value } } so existing handlers keep working.

const toKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const fromKey = (value) => {
  if (!value) return undefined;
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const DateField = React.forwardRef(
  ({ value, onChange, placeholder = "Pick a date", className = "", id, disabled, min, max, ...rest }, ref) => {
    const [open, setOpen] = React.useState(false);
    const selected = fromKey(value);
    const emit = (next) => onChange?.({ target: { value: next ? toKey(next) : "" } });
    const minDate = fromKey(min);
    const maxDate = fromKey(max);
    const disabledDays = [];
    if (minDate) disabledDays.push({ before: minDate });
    if (maxDate) disabledDays.push({ after: maxDate });

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            className={`date-field ${selected ? "has-value" : ""} ${className}`}
            {...rest}
          >
            <CalendarDays size={15} strokeWidth={1.8} />
            <span className="date-field-value">
              {selected
                ? selected.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                : placeholder}
            </span>
            {selected && !disabled && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear date"
                className="date-field-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  emit(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    emit(null);
                  }
                }}
              >
                <X size={13} />
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="date-popover">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            disabled={disabledDays.length ? disabledDays : undefined}
            onSelect={(day) => {
              emit(day || null);
              if (day) setOpen(false);
            }}
            initialFocus
          />
          <div className="date-popover-foot">
            <button type="button" onClick={() => emit(null)} disabled={!selected}>
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                emit(new Date());
                setOpen(false);
              }}
            >
              Today
            </button>
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);
DateField.displayName = "DateField";

export default DateField;
